import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faPlay,
  faStop,
  faPhone,
  faCheck,
  faSpinner,
  faMagicWandSparkles,
  faCreditCard,
  faShieldAlt,
  faGift,
} from "@fortawesome/free-solid-svg-icons";
import { RetellWebClient } from "retell-client-js-sdk";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import "../../styles/app/AgentWizard.css";
import logoIMG from "../../assets/app/Nav/LOGO-FONDO-NEGRO.png";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK || "");

const retellWebClient = new RetellWebClient();

// ── Helpers ──
const getBaseUrl = () => {
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return "http://localhost:5171";
  }
  return "https://api.tuagentevirtual.ai";
};

const AI_AGENTS = [
  { id: "sofia", name: "Sofia", image: "/images/agent-avatar.png", voiceId: "11labs-Myra", gender: "female", personality: "Warm & Professional" },
  { id: "luna", name: "Luna", image: "/images/agent-luna.png", voiceId: "11labs-Paola", gender: "female", personality: "Calm & Empathetic" },
  { id: "aria", name: "Aria", image: "/images/agent-aria.png", voiceId: "11labs-Myra", gender: "female", personality: "Smart & Efficient" },
  { id: "maya", name: "Maya", image: "/images/agent-maya.png", voiceId: "11labs-Paola", gender: "female", personality: "Energetic & Friendly" },
  { id: "zoe", name: "Zoe", image: "/images/agent-zoe.png", voiceId: "11labs-Myra", gender: "female", personality: "Elegant & Polished" },
  { id: "lexi", name: "Lexi", image: "/images/agent-lexi.png", voiceId: "11labs-Paola", gender: "female", personality: "Bold & Confident" },
  { id: "nova", name: "Nova", image: "/images/agent-nova.png", voiceId: "11labs-Myra", gender: "female", personality: "Creative & Vibrant" },
  { id: "iris", name: "Iris", image: "/images/agent-iris.png", voiceId: "11labs-Paola", gender: "female", personality: "Sophisticated & Kind" },
  { id: "leo", name: "Leo", image: "/images/agent-leo.png", voiceId: "11labs-Adrian", gender: "male", personality: "Charismatic & Reliable" },
  { id: "max", name: "Max", image: "/images/agent-max.png", voiceId: "11labs-Adrian", gender: "male", personality: "Friendly & Approachable" },
  { id: "dan", name: "Dan", image: "/images/agent-dan.png", voiceId: "11labs-Adrian", gender: "male", personality: "Sharp & Professional" },
  { id: "alex", name: "Alex", image: "/images/agent-alex.png", voiceId: "11labs-Adrian", gender: "male", personality: "Calm & Trustworthy" },
];

const VOICES = [
  { id: "11labs-Adrian", name: "Adrian", desc: "Professional & Warm", gender: "male" },
  { id: "11labs-Myra", name: "Myra", desc: "Energetic & Friendly", gender: "female" },
  { id: "11labs-Paola", name: "Paola", desc: "Soft & Relaxed", gender: "female" },
];

const INDUSTRIES = [
  "Restaurant / Hospitality",
  "Real Estate",
  "Health / Medical",
  "Retail / Commerce",
  "Technology",
  "Professional Services",
  "Education",
  "Logistics / Shipping",
  "Finance / Insurance",
  "Automotive",
  "Other",
];

const CALL_TYPES = [
  "Prospective Client Inquiry",
  "Service / Product Info",
  "Pricing Questions",
  "Appointment Scheduling",
  "General Business Inquiry",
  "Partnership Inquiry",
];

const CHANNELS = [
  "Inbound calls",
  "Outbound calls",
  "Web chat",
  "WhatsApp",
];

const generateGreeting = (lang: string, businessName: string, agentName: string) => {
  switch (lang) {
    case "es":
      return `Hola, gracias por llamar a ${businessName}. Soy ${agentName}, ¿en qué puedo ayudarte hoy?`;
    case "pt":
      return `Olá, obrigado por ligar para ${businessName}. Sou ${agentName}, como posso ajudá-lo hoje?`;
    default:
      return `Hi, thank you for calling ${businessName}! I'm ${agentName}, how can I help you today?`;
  }
};

// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════
const AgentWizard: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const urlFromQuery = searchParams.get("url") || "";

  // ── Global State ──
  const [currentStep, setCurrentStep] = useState(0); // 0=loading/scraping, 1-6=wizard steps
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId, setSessionId] = useState("");

  // ── Step 1: Business Profile ──
  const [businessProfile, setBusinessProfile] = useState<any>(null);
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [country, setCountry] = useState("");
  const [language, setLanguage] = useState("en");

  // ── Step 2: Capabilities ──
  const [callTypes, setCallTypes] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>(["Inbound calls"]);
  const [transferPolicy, setTransferPolicy] = useState("On request");
  const [afterHours, setAfterHours] = useState("AI handles full call");

  // ── Step 3: Identity ──
  const agentFromQuery = searchParams.get("agent") || "";
  const initialAgent = AI_AGENTS.find(a => a.id === agentFromQuery) || AI_AGENTS[0];
  const [selectedAgent, setSelectedAgent] = useState(initialAgent);
  const [agentName, setAgentName] = useState(initialAgent.name);
  const [greeting, setGreeting] = useState("");
  const [selectedVoice, setSelectedVoice] = useState(initialAgent.voiceId);

  // ── Step 4: Lead Capture ──
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  // ── Step 5: Stripe ──
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
  const [trialActivated, setTrialActivated] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);

  // ── Step 6: Test Drive ──
  const [callStatus, setCallStatus] = useState<"idle" | "connecting" | "active">("idle");
  const [agentCreated, setAgentCreated] = useState(false);
  const [createdAgentInfo, setCreatedAgentInfo] = useState<any>(null);
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);

  // ── TOTAL STEPS ──
  const TOTAL_STEPS = 6;

  // ── Retell Event Listeners ──
  useEffect(() => {
    retellWebClient.on("call_started", () => setCallStatus("active"));
    retellWebClient.on("call_ended", () => setCallStatus("idle"));
    retellWebClient.on("error", () => setCallStatus("idle"));
    return () => { retellWebClient.stopCall(); };
  }, []);

  // ── Handle Stripe return & auto-scrape on mount ──
  useEffect(() => {
    const stripeSuccess = searchParams.get("stripe_success");
    const stripeSessionId = searchParams.get("session_id");
    const wizardSession = searchParams.get("wizard_session");
    const stripeCancel = searchParams.get("stripe_cancel");

    if (stripeSuccess === "true" && stripeSessionId && wizardSession) {
      // Returning from Stripe checkout — verify and go to Step 6
      setSessionId(wizardSession);
      setIsLoading(true);
      verifyStripeAndContinue(stripeSessionId, wizardSession);
      return;
    }

    if (stripeCancel === "true" && wizardSession) {
      // User cancelled Stripe — go back to Step 5
      setSessionId(wizardSession);
      setIsLoading(false);
      setCurrentStep(5);
      return;
    }

    if (urlFromQuery) {
      handleScrape(urlFromQuery);
    } else {
      setIsLoading(false);
      setCurrentStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyStripeAndContinue = async (stripeSessionId: string, wizSession: string) => {
    try {
      const response = await fetch(`${getBaseUrl()}/api/Stripe/verify/${stripeSessionId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.verified) {
          setTrialActivated(true);
          setSessionId(wizSession);
          setCurrentStep(6);
          handleCreateAgentAfterStripe(wizSession);
          return;
        }
      }
      // Verification failed — go back to Step 5
      setCurrentStep(5);
    } catch (err) {
      console.error("[Wizard] Stripe verify error:", err);
      setCurrentStep(5);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScrape = async (url: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${getBaseUrl()}/api/AgentFunnel/scrape`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) throw new Error("Scrape failed");

      const data = await response.json();
      setSessionId(data.sessionId);
      setBusinessProfile(data.businessProfile);

      // Pre-fill from scraped data
      setBusinessName(data.businessProfile?.businessName || "");
      setIndustry(data.businessProfile?.industry || "");
      setCountry(data.businessProfile?.country || "");
      setLanguage(data.businessProfile?.language || "en");

      const bName = data.businessProfile?.businessName || "your company";
      const lang = data.businessProfile?.language || "en";
      setGreeting(generateGreeting(lang, bName, "Sofia"));

      setCurrentStep(1);
    } catch (err) {
      console.error("[Wizard] Scrape error:", err);
      setCurrentStep(1);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Save progress to backend on every step change ──
  const saveProgress = useCallback(
    async (step: number) => {
      if (!sessionId) return;
      try {
        await fetch(`${getBaseUrl()}/api/AgentFunnel/update/${sessionId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: contactEmail,
            phone: contactPhone,
            contactName,
            currentStep: step,
            agentConfigJson: JSON.stringify({
              agentName,
              voiceId: selectedVoice,
              greeting,
              callTypes,
              channels,
              transferPolicy,
              afterHours,
            }),
          }),
        });
      } catch (err) {
        console.error("[Wizard] Save progress error:", err);
      }
    },
    [sessionId, contactEmail, contactPhone, contactName, agentName, selectedVoice, greeting, callTypes, channels, transferPolicy, afterHours]
  );

  const goToStep = (step: number) => {
    saveProgress(step);
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Stripe Embedded Checkout (Step 5 trigger) ──
  const handleStripeCheckout = async () => {
    if (!sessionId) return;
    setIsRedirectingToStripe(true);
    try {
      await saveProgress(5);
      const response = await fetch(`${getBaseUrl()}/api/Stripe/create-embedded-checkout/${sessionId}`, {
        method: "POST",
      });
      if (!response.ok) throw new Error("Embedded checkout creation failed");
      const data = await response.json();
      setStripeClientSecret(data.clientSecret);
    } catch (err) {
      console.error("[Wizard] Stripe embedded checkout error:", err);
      setIsRedirectingToStripe(false);
    }
  };

  // ── Create Agent in Retell (Step 6 trigger, after Stripe) ──
  const handleCreateAgent = async () => {
    if (!sessionId) return;
    setIsCreatingAgent(true);
    try {
      await saveProgress(6);
      const response = await fetch(`${getBaseUrl()}/api/AgentFunnel/create-agent/${sessionId}`, {
        method: "POST",
      });
      if (!response.ok) throw new Error("Agent creation failed");
      const data = await response.json();
      setCreatedAgentInfo(data);
      setAgentCreated(true);
    } catch (err) {
      console.error("[Wizard] Agent creation error:", err);
    } finally {
      setIsCreatingAgent(false);
    }
  };

  // ── Create Agent after Stripe return (uses wizardSession from URL) ──
  const handleCreateAgentAfterStripe = async (wizSession: string) => {
    setIsCreatingAgent(true);
    try {
      const response = await fetch(`${getBaseUrl()}/api/AgentFunnel/create-agent/${wizSession}`, {
        method: "POST",
      });
      if (!response.ok) throw new Error("Agent creation failed");
      const data = await response.json();
      setCreatedAgentInfo(data);
      setAgentCreated(true);
    } catch (err) {
      console.error("[Wizard] Agent creation error:", err);
    } finally {
      setIsCreatingAgent(false);
    }
  };

  // ── Start WebRTC Test Call ──
  const handleTestCall = async () => {
    if (callStatus === "active") {
      retellWebClient.stopCall();
      setCallStatus("idle");
      return;
    }

    setCallStatus("connecting");
    try {
      const response = await fetch(`${getBaseUrl()}/api/AgentFunnel/test-call/${sessionId}`, {
        method: "POST",
      });

      if (!response.ok) throw new Error("Test call failed");

      const data = await response.json();
      await retellWebClient.startCall({ accessToken: data.access_token });
    } catch (err) {
      console.error("[Wizard] Test call error:", err);
      setCallStatus("idle");
    }
  };

  const toggleArrayItem = (arr: string[], item: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item]);
  };

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════
  return (
    <div className="agent-wizard-page">
      {/* ── Branded Header ── */}
      <header className="wizard-header">
        <div className="wizard-header-inner">
          <Link to="/" className="wizard-logo-link">
            <img src={logoIMG} alt="TuAgente Virtual AI" className="wizard-logo-img" />
          </Link>
          <div className="wizard-header-tagline">
            {t("wizard.headerTagline", "Your AI agent is just minutes away")}
          </div>
        </div>
      </header>

      {/* ── Step Progress Bar ── */}
      <div className="wizard-progress-bar">
        <div className="wizard-progress-inner">
          {[
            { icon: "🏢", label: t("wizard.stepLabel1", "Business") },
            { icon: "⚙️", label: t("wizard.stepLabel2", "Skills") },
            { icon: "🎤", label: t("wizard.stepLabel3", "Personality") },
            { icon: "📧", label: t("wizard.stepLabel4", "Contact") },
            { icon: "💳", label: t("wizard.stepLabel5", "Activate") },
            { icon: "🚀", label: t("wizard.stepLabel6", "Launch") },
          ].map((step, i) => {
            const stepNum = i + 1;
            const isActive = currentStep === stepNum;
            const isCompleted = currentStep > stepNum;
            return (
              <React.Fragment key={i}>
                <div className={`wz-step-item ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}>
                  <div className="wz-step-circle">
                    {isCompleted ? (
                      <FontAwesomeIcon icon={faCheck} />
                    ) : (
                      <span className="wz-step-icon">{step.icon}</span>
                    )}
                  </div>
                  <span className="wz-step-label">{step.label}</span>
                </div>
                {i < TOTAL_STEPS - 1 && (
                  <div className={`wz-step-line ${isCompleted ? "completed" : ""}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Main Content ── */}
      <main className="wizard-main">
        {isLoading ? (
          <div className="wizard-card">
            <div className="wizard-loading">
              <div className="wizard-spinner" />
              <p>
                <FontAwesomeIcon icon={faMagicWandSparkles} style={{ marginRight: 8 }} />
                {t("wizard.analyzing", "Analyzing your website with AI...")}
              </p>
              <p style={{ color: "#64748b", fontSize: 13, marginTop: 8 }}>
                {t("wizard.analyzingSubtitle", "We're reading your site to pre-fill everything for you.")}
              </p>
            </div>
          </div>
        ) : (
          <div className="wizard-card" key={currentStep}>
            {/* ════════════════════════════════
                STEP 1: Business Profile
               ════════════════════════════════ */}
            {currentStep === 1 && (
              <>
                <div className="wizard-step-badge">
                  {t("wizard.step", "Step")} 1 / {TOTAL_STEPS}
                </div>
                <h2 className="wizard-step-title">
                  {t("wizard.step1Title", "Getting to know your business")}
                </h2>
                <p className="wizard-step-subtitle">
                  {t("wizard.step1Subtitle", "We've analyzed your website. Please confirm or adjust the details below.")}
                </p>

                {businessProfile && (
                  <div className="profile-review-card">
                    <h3>{businessProfile.businessName || "Your Business"}</h3>
                    <p>{businessProfile.description || ""}</p>
                    {businessProfile.products && businessProfile.products.length > 0 && (
                      <ul className="profile-products-list">
                        {businessProfile.products.map((p: any, idx: number) => (
                          <li key={idx}>
                            <strong>{p.name}:</strong> {p.description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="wz-row">
                  <div className="wz-field">
                    <label className="wz-label">{t("wizard.businessName", "Business Name")}</label>
                    <input
                      className="wz-input"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      id="wz-business-name"
                    />
                  </div>
                  <div className="wz-field">
                    <label className="wz-label">{t("wizard.industry", "Industry")}</label>
                    <select className="wz-select" value={industry} onChange={(e) => setIndustry(e.target.value)} id="wz-industry">
                      <option value="">{t("wizard.selectIndustry", "Select industry")}</option>
                      {INDUSTRIES.map((ind) => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="wz-row">
                  <div className="wz-field">
                    <label className="wz-label">{t("wizard.country", "Country")}</label>
                    <input
                      className="wz-input"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      id="wz-country"
                    />
                  </div>
                  <div className="wz-field">
                    <label className="wz-label">{t("wizard.language", "Agent Language")}</label>
                    <select className="wz-select" value={language} onChange={(e) => { setLanguage(e.target.value); setGreeting(generateGreeting(e.target.value, businessName || "us", agentName)); }} id="wz-language">
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="pt">Português</option>
                    </select>
                  </div>
                </div>

                <div className="wizard-nav">
                  <div />
                  <button className="wz-btn wz-btn-next" onClick={() => goToStep(2)} id="wz-next-1">
                    {t("wizard.next", "Next")} <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
                  </button>
                </div>
              </>
            )}

            {/* ════════════════════════════════
                STEP 2: Capabilities
               ════════════════════════════════ */}
            {currentStep === 2 && (
              <>
                <div className="wizard-step-badge">
                  {t("wizard.step", "Step")} 2 / {TOTAL_STEPS}
                </div>
                <h2 className="wizard-step-title">
                  {t("wizard.step2Title", "What should your agent do?")}
                </h2>
                <p className="wizard-step-subtitle">
                  {t("wizard.step2Subtitle", "Tell us what kind of calls your AI should handle and how.")}
                </p>

                <label className="wz-label">{t("wizard.callTypes", "Call Types & Goals")}</label>
                <div className="wz-checkbox-group">
                  {CALL_TYPES.map((ct) => (
                    <label key={ct} className="wz-checkbox-label">
                      <input
                        type="checkbox"
                        checked={callTypes.includes(ct)}
                        onChange={() => toggleArrayItem(callTypes, ct, setCallTypes)}
                      />
                      {ct}
                    </label>
                  ))}
                </div>

                <label className="wz-label">{t("wizard.channels", "Communication Channels")}</label>
                <div className="wz-checkbox-group">
                  {CHANNELS.map((ch) => (
                    <label key={ch} className="wz-checkbox-label">
                      <input
                        type="checkbox"
                        checked={channels.includes(ch)}
                        onChange={() => toggleArrayItem(channels, ch, setChannels)}
                      />
                      {ch}
                    </label>
                  ))}
                </div>

                <div className="wz-row">
                  <div className="wz-field">
                    <label className="wz-label">{t("wizard.transfer", "When to Transfer")}</label>
                    <select className="wz-select" value={transferPolicy} onChange={(e) => setTransferPolicy(e.target.value)}>
                      <option value="On request">On request</option>
                      <option value="Qualified leads only">Qualified leads only</option>
                      <option value="Never">Never</option>
                    </select>
                  </div>
                  <div className="wz-field">
                    <label className="wz-label">{t("wizard.afterHours", "After-Hours Behavior")}</label>
                    <select className="wz-select" value={afterHours} onChange={(e) => setAfterHours(e.target.value)}>
                      <option value="AI handles full call">AI handles full call</option>
                      <option value="AI captures & callback">AI captures & callback</option>
                      <option value="Emergency only">Emergency only</option>
                    </select>
                  </div>
                </div>

                <div className="wizard-nav">
                  <button className="wz-btn wz-btn-back" onClick={() => goToStep(1)}>
                    <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 8 }} /> {t("wizard.back", "Back")}
                  </button>
                  <button className="wz-btn wz-btn-next" onClick={() => goToStep(3)} id="wz-next-2">
                    {t("wizard.next", "Next")} <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
                  </button>
                </div>
              </>
            )}

            {/* ════════════════════════════════
                STEP 3: Agent Identity
               ════════════════════════════════ */}
            {currentStep === 3 && (
              <>
                <div className="wizard-step-badge">
                  {t("wizard.step", "Step")} 3 / {TOTAL_STEPS}
                </div>
                <h2 className="wizard-step-title">
                  {t("wizard.step3Title", "Choose your AI receptionist")}
                </h2>
                <p className="wizard-step-subtitle">
                  {t("wizard.step3Subtitle", "Select the agent that best represents your brand. Each has a unique voice and personality.")}
                </p>

                <div className="agent-selector-grid">
                  {AI_AGENTS.map((agent) => (
                    <div
                      key={agent.id}
                      className={`agent-card ${selectedAgent.id === agent.id ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedAgent(agent);
                        setAgentName(agent.name);
                        setSelectedVoice(agent.voiceId);
                        setGreeting(generateGreeting(language, businessName || "us", agent.name));
                      }}
                    >
                      <div className="agent-card-avatar">
                        <img src={agent.image} alt={agent.name} />
                      </div>
                      <div className="agent-card-name">{agent.name}</div>
                      <div className="agent-card-personality">{agent.personality}</div>
                      {selectedAgent.id === agent.id && (
                        <div className="agent-card-check">
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="wz-row" style={{ marginTop: 24 }}>
                  <div className="wz-field">
                    <label className="wz-label">{t("wizard.agentName", "Agent Name")}</label>
                    <input
                      className="wz-input"
                      value={agentName}
                      onChange={(e) => {
                        setAgentName(e.target.value);
                        setGreeting(generateGreeting(language, businessName || "us", e.target.value));
                      }}
                      placeholder="Sofia"
                      id="wz-agent-name"
                    />
                  </div>
                  <div className="wz-field">
                    <label className="wz-label">{t("wizard.language", "Agent Language")}</label>
                    <select className="wz-select" value={language} onChange={(e) => { setLanguage(e.target.value); setGreeting(generateGreeting(e.target.value, businessName || "us", agentName)); }}>
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="pt">Português</option>
                    </select>
                  </div>
                </div>

                <div className="wz-field">
                  <label className="wz-label">{t("wizard.greeting", "Greeting Phrase")}</label>
                  <textarea
                    className="wz-textarea"
                    value={greeting}
                    onChange={(e) => setGreeting(e.target.value)}
                    rows={3}
                    id="wz-greeting"
                  />
                </div>

                <div className="wizard-nav">
                  <button className="wz-btn wz-btn-back" onClick={() => goToStep(2)}>
                    <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 8 }} /> {t("wizard.back", "Back")}
                  </button>
                  <button className="wz-btn wz-btn-next" onClick={() => goToStep(4)} id="wz-next-3">
                    {t("wizard.next", "Next")} <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
                  </button>
                </div>
              </>
            )}

            {/* ════════════════════════════════
                STEP 4: Contact Info (Lead Gate)
               ════════════════════════════════ */}
            {currentStep === 4 && (
              <>
                <div className="wizard-step-badge">
                  {t("wizard.step", "Step")} 4 / {TOTAL_STEPS}
                </div>
                <h2 className="wizard-step-title">
                  {t("wizard.step4Title", "Almost there! Let's save your agent")}
                </h2>
                <p className="wizard-step-subtitle">
                  {t("wizard.step4Subtitle", "Enter your contact info so we can finalize your agent and send you the access details.")}
                </p>

                <div className="wz-field">
                  <label className="wz-label">{t("wizard.yourName", "Your Name")}</label>
                  <input
                    className="wz-input"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="John Doe"
                    id="wz-contact-name"
                  />
                </div>

                <div className="wz-row">
                  <div className="wz-field">
                    <label className="wz-label">{t("wizard.email", "Email")} *</label>
                    <input
                      className="wz-input"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="you@company.com"
                      required
                      id="wz-contact-email"
                    />
                  </div>
                  <div className="wz-field">
                    <label className="wz-label">{t("wizard.phone", "Phone")}</label>
                    <input
                      className="wz-input"
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      placeholder="+1 555 123 4567"
                      id="wz-contact-phone"
                    />
                  </div>
                </div>

                <div style={{ textAlign: "center", padding: "16px 0", color: "#64748b", fontSize: 13 }}>
                  🔒 {t("wizard.privacyNote", "Your data is secure and will not be shared with third parties.")}
                </div>

                <div className="wizard-nav">
                  <button className="wz-btn wz-btn-back" onClick={() => goToStep(3)}>
                    <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 8 }} /> {t("wizard.back", "Back")}
                  </button>
                  <button
                    className="wz-btn wz-btn-next"
                    disabled={!contactEmail}
                    onClick={() => goToStep(5)}
                    id="wz-next-4"
                  >
                    {t("wizard.next", "Next")} <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
                  </button>
                </div>
              </>
            )}

            {/* ════════════════════════════════
                STEP 5: Subscription Plan (Stripe)
               ════════════════════════════════ */}
            {currentStep === 5 && (
              <>
                <div className="wizard-step-badge">
                  {t("wizard.step", "Step")} 5 / {TOTAL_STEPS}
                </div>
                <h2 className="wizard-step-title">
                  {t("wizard.step5StripeTitle", "Activate your AI agent")}
                </h2>
                <p className="wizard-step-subtitle">
                  {t("wizard.step5StripeSubtitle", "Start with a 30-day free trial. No charges today.")}
                </p>

                {stripeClientSecret ? (
                  /* ── Embedded Stripe Checkout Form ── */
                  <div className="stripe-embedded-container">
                    <EmbeddedCheckoutProvider
                      stripe={stripePromise}
                      options={{ clientSecret: stripeClientSecret }}
                    >
                      <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                  </div>
                ) : (
                  /* ── Plan Summary Card ── */
                  <>
                    <div className="stripe-plan-card">
                      <div className="plan-badge-free">
                        <FontAwesomeIcon icon={faGift} style={{ marginRight: 6 }} />
                        {t("wizard.trialBadge", "30 Days FREE")}
                      </div>

                      <h3 className="plan-name">TuAgente Virtual AI</h3>
                      <div className="plan-price">
                        <span className="plan-price-amount">$0</span>
                        <span className="plan-price-period"> {t("wizard.today", "today")}</span>
                      </div>
                      <div className="plan-trial-note">
                        {t("wizard.trialNote", "Then $99/month after your 30-day free trial. Cancel anytime.")}
                      </div>

                      <ul className="plan-features">
                        <li><FontAwesomeIcon icon={faCheck} className="plan-check" /> {t("wizard.feature1", "AI voice agent with custom personality")}</li>
                        <li><FontAwesomeIcon icon={faCheck} className="plan-check" /> {t("wizard.feature2", "24/7 automated call handling")}</li>
                        <li><FontAwesomeIcon icon={faCheck} className="plan-check" /> {t("wizard.feature3", "Intelligent multilingual support")}</li>
                        <li><FontAwesomeIcon icon={faCheck} className="plan-check" /> {t("wizard.feature4", "Real-time call analytics dashboard")}</li>
                        <li><FontAwesomeIcon icon={faCheck} className="plan-check" /> {t("wizard.feature5", "Cancel anytime, no commitment")}</li>
                      </ul>

                      <div className="plan-security">
                        <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 6 }} />
                        {t("wizard.securePayment", "Secure payment powered by Stripe. 256-bit SSL encryption.")}
                      </div>
                    </div>

                    <div className="wizard-nav">
                      <button className="wz-btn wz-btn-back" onClick={() => goToStep(4)}>
                        <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 8 }} /> {t("wizard.back", "Back")}
                      </button>
                      <button
                        className="wz-btn wz-btn-stripe"
                        onClick={handleStripeCheckout}
                        disabled={isRedirectingToStripe}
                        id="wz-start-trial"
                      >
                        {isRedirectingToStripe ? (
                          <><FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} /> {t("wizard.loadingCheckout", "Loading checkout...")}</>
                        ) : (
                          <><FontAwesomeIcon icon={faCreditCard} style={{ marginRight: 8 }} /> {t("wizard.startTrial", "Start Free Trial")}</>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {/* ════════════════════════════════
                STEP 6: Agent Creation + Test Drive
               ════════════════════════════════ */}
            {currentStep === 6 && (
              <>
                <div className="wizard-step-badge">
                  {t("wizard.step", "Step")} 6 / {TOTAL_STEPS}
                </div>

                {isCreatingAgent ? (
                  <div className="wizard-loading">
                    <div className="wizard-spinner" />
                    <p>{t("wizard.creatingAgent", "Creating your AI agent...")}</p>
                    <p style={{ color: "#64748b", fontSize: 13, marginTop: 8 }}>
                      {t("wizard.creatingSubtitle", "This takes about 10 seconds. We're configuring the voice, personality, and knowledge base.")}
                    </p>
                  </div>
                ) : agentCreated && createdAgentInfo ? (
                  <div className="test-drive-container">
                    <h2 className="wizard-step-title" style={{ marginBottom: 6 }}>
                      🎉 {t("wizard.step5Title", "A new AI employee is born!")}
                    </h2>
                    <p className="wizard-step-subtitle">
                      {t("wizard.step5Subtitle", "Your agent is live. Test it right now by talking through your browser!")}
                    </p>

                    <div className="test-drive-agent-card">
                      <div className="td-agent-avatar">
                        <img src={selectedAgent.image} alt={createdAgentInfo.agentName} />
                      </div>
                      <div className="td-agent-name">{createdAgentInfo.agentName}</div>
                      <div className="td-agent-role">AI Receptionist</div>
                      <div className="td-agent-company">{createdAgentInfo.businessName}</div>
                    </div>

                    <button
                      className={`test-call-button ${callStatus === "active" ? "active-call" : ""}`}
                      onClick={handleTestCall}
                      disabled={callStatus === "connecting"}
                      id="wz-test-call"
                    >
                      {callStatus === "idle" && (
                        <>
                          <FontAwesomeIcon icon={faPhone} />
                          {t("wizard.testCall", "Make a Test Call")}
                        </>
                      )}
                      {callStatus === "connecting" && (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin />
                          {t("wizard.connecting", "Connecting...")}
                        </>
                      )}
                      {callStatus === "active" && (
                        <>
                          <FontAwesomeIcon icon={faStop} />
                          {t("wizard.hangUp", "Hang Up")}
                        </>
                      )}
                    </button>

                    <div className={`test-call-status ${callStatus === "active" ? "connected" : ""}`}>
                      {callStatus === "idle" && t("wizard.testIdleHint", "Click the button and start talking — your agent is listening.")}
                      {callStatus === "connecting" && t("wizard.testConnecting", "Establishing secure connection...")}
                      {callStatus === "active" && t("wizard.testActive", "🟢 Live — Speak now! Your agent is responding.")}
                    </div>

                    <div style={{ marginTop: 40 }}>
                      <Link to="/" className="wz-btn wz-btn-back" style={{ textDecoration: "none" }}>
                        {t("wizard.backToHome", "← Back to Home")}
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="wizard-loading">
                    <p style={{ color: "#ef4444" }}>
                      {t("wizard.createError", "Something went wrong. Please go back and try again.")}
                    </p>
                    <button className="wz-btn wz-btn-back" onClick={() => goToStep(4)} style={{ marginTop: 20 }}>
                      <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 8 }} /> {t("wizard.back", "Back")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AgentWizard;
